import React, { useState, useEffect } from 'react';

import { AudioContainer, DraggableAudio, useAudioPlayerStore } from '@atlassian/ai-audio';

import type { EmojiProvider } from '@atlaskit/emoji';
import { EmojiResource } from '@atlaskit/emoji/resource';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { ConditionalCreateSpaceDrawer } from '@confluence/create-space/entry-points/createSpace';
import {
	ConditionalImportContentModal,
	ConditionalImportContentDrawer,
} from '@confluence/import-content/entry-points/importContent';
import { ForgeGlobalKeyboardShortcuts } from '@confluence/forge-ui/entry-points/ForgeGlobalKeyboardShortcuts';
import { StorageEnforcement } from '@confluence/storage-enforcement/entry-points/StorageEnforcement';
import { LoomAiMeetingNotesPostProvisionFlag } from '@confluence/experiment-loom-ai-meeting-notes-in-confluence-cross-flow/entry-points/LoomAiMeetingNotesPostProvisionFlag';
import { RovoVideoModal } from '@confluence/rovo-video-modal/entry-points/RovoVideoModal';
import { TrialFlags } from '@confluence/trial-components/entry-points/TrialFlags';
import { LazyRestrictionsDialogMountPoint } from '@confluence/restrictions/entry-points/RestrictionsDialogTrigger';
import { ProgressBar } from '@confluence/global-progress-bar';
import { useAddUFOCustomDataForNavType } from '@confluence/nav4-enabled/entry-points/useAddUFOCustomDataForNavType';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { MisalignedNavVersionsFlag } from '@confluence/nav-versions-misaligned';
import { TrialPlanComparisonModalMountComponent } from '@confluence/persistent-upgrade/entry-points/lazyTrialPlanComparisonModal';
import { StandardToPremiumModalMountComponent } from '@confluence/experiment-edition-awareness-for-standard-to-premium';
import { ModernizeChangeboarding } from '@confluence/modernize-changeboarding';
import { ConfluenceSmartCreationModalLoader } from '@confluence/smart-create/entry-points/smartCreationModal';
import { audioFeatures } from '@confluence/audio/entry-points/audioFeatures';
import { CrossJoinNudgesWithFlags } from '@confluence/cross-join-nudges';
import { fg } from '@confluence/feature-gating';
import { UpgradeFreePoolExperimentModalPostUpgradeModalLoadable } from '@confluence/experiment-upgrade-free-pool-target/entry-points/post-upgrade-modal';
import { UpgradeFreePoolExperimentModalPreUpgradeModalLoadable } from '@confluence/experiment-upgrade-free-pool-target/entry-points/pre-upgrade-modal';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags';

type AudioContainerWrapperProps = {
	emojiProvider: EmojiProvider;
} & WithFlagsProps;

// TODO: hardcoding cloudId and activationId for now
// once we have the light-weight wrapper we can destructure from sessionData
const AudioContainerWithFlags = withFlags(
	({ emojiProvider, flags }: AudioContainerWrapperProps) => (
		<DraggableAudio>
			<AudioContainer
				product="confluence"
				cloudId="DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5"
				activationId="0591fa3a-8f53-4cfe-8f24-2f7be5d4011b"
				emojiProvider={emojiProvider}
				audioFeatures={audioFeatures}
				flags={flags as any}
			/>
		</DraggableAudio>
	),
);

/**
 * Use this component to render global components that should be rendered on every page.
 */
export const GlobalComponents = () => {
	const isNav4DisplayedToUser = useIsNav4Enabled();
	const { isOpen: isAudioPlayerOpen } = useAudioPlayerStore();
	const { userId, cloudId } = useSessionData();
	const [emojiProvider, setEmojiProvider] = useState<EmojiProvider | undefined>(undefined);

	useAddUFOCustomDataForNavType();

	useEffect(() => {
		//getEmojiProvider returns a promise, but getEmojiProvider requires it to be resolved. This middle step ensures the promise has returned beore the value is passed
		getEmojiProvider(EmojiResource, { cloudId, userId }).then(setEmojiProvider);
	}, [cloudId, userId]);

	return (
		<>
			<ErrorBoundary
				attribution={Attribution.DISCO}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Disco',
				}}
			>
				<ConditionalCreateSpaceDrawer />
				<MisalignedNavVersionsFlag />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.SMARTS}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Smarts',
				}}
			>
				{fg('aifc_create_enabled') && <ConfluenceSmartCreationModalLoader />}
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.CC_IMPORTER}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Importer',
				}}
			>
				<ConditionalImportContentModal />
				<ConditionalImportContentDrawer />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ECOSYSTEM}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Ecosystem',
				}}
			>
				<ForgeGlobalKeyboardShortcuts />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ADMIN_EXPERIENCE}
				attributes={{
					errorBoundaryId: 'GlobalComponents-AdminExperience',
				}}
			>
				<StorageEnforcement />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthLuna',
				}}
			>
				<TrialPlanComparisonModalMountComponent />
				<StandardToPremiumModalMountComponent />
				<RovoVideoModal />
				<TrialFlags />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
				<LazyRestrictionsDialogMountPoint />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.FLYWHEEL_ACTIVATION}>
				<ModernizeChangeboarding />
			</ErrorBoundary>

			{isNav4DisplayedToUser && (
				<ErrorBoundary attribution={Attribution.BACKBONE}>
					<ProgressBar isNav4 />
				</ErrorBoundary>
			)}

			<ErrorBoundary
				attribution={Attribution.GROWTH_PUMA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthPuma',
				}}
			>
				<CrossJoinNudgesWithFlags execNudge={fg('puma-256-cjn-global-home')} />
			</ErrorBoundary>

			{fg('loom_ai_meeting_notes_in_confluence_killswitch') && (
				<ErrorBoundary attribution={Attribution.GROWTH_MULTIPLIERS}>
					<LoomAiMeetingNotesPostProvisionFlag />
				</ErrorBoundary>
			)}

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthArtemis',
				}}
			>
				{fg('twcg-206_confluence_upgrade_permanent_free_fg') && (
					<>
						<UpgradeFreePoolExperimentModalPreUpgradeModalLoadable />
						<UpgradeFreePoolExperimentModalPostUpgradeModalLoadable />
					</>
				)}
			</ErrorBoundary>

			{isAudioPlayerOpen && emojiProvider && (
				<AudioContainerWithFlags emojiProvider={emojiProvider} />
			)}
		</>
	);
};
